import { useEffect, useState, type JSX, type ReactNode } from "react";

// The one confirmation dialog every destructive verb asks through, so a
// person always reads the same shape before the act: what it is, what it
// does, and one red button. A typed confirmation mirrors the server's rule
// so the button never promises what the route refuses.

/** What the dialog takes: the words, the tone, the busy and error state, and the two callbacks. */
export type ConfirmModalProps = {
  open: boolean;
  // The mono head label ("Kill Run", "Delete Request").
  title: string;
  // The question, as an h3 ("Kill Run 01K…?").
  heading: ReactNode;
  // The body: what the act does, listed plainly.
  children?: ReactNode;
  confirmLabel: string;
  // What the confirm button reads while the act runs ("Killing…"): required,
  // so no confirm can sit dimmed and silent.
  busyLabel: string;
  cancelLabel?: string;
  // The confirm button's tone: danger (red outline) for destruction,
  // primary for a consequential but recoverable act.
  tone?: "danger" | "primary";
  busy?: boolean;
  error?: string | null;
  // Refuse the confirm regardless of the typed word (a live-Run refusal).
  disabled?: boolean;
  // Typed confirmation: `expected` is what the person must type; `label`
  // names it above the box.
  typed?: { expected: string; label: ReactNode; placeholder?: string };
  confirmTestId?: string;
  cancelTestId?: string;
  onConfirm: (typed: string) => void;
  onCancel: () => void;
};

/**
 * Renders the confirmation dialog while `open`, and nothing otherwise. The
 * confirm button enables only when nothing is in flight, `disabled` is not
 * set and, for a typed confirmation, the box matches `expected` exactly.
 * Escape, Cancel and the backdrop call `onCancel` unless `busy`; the
 * kill, delete and remove controls across the app render it.
 */
export function ConfirmModal({
  open,
  title,
  heading,
  children,
  confirmLabel,
  busyLabel,
  cancelLabel = "Cancel",
  tone = "danger",
  busy = false,
  error = null,
  disabled = false,
  typed,
  confirmTestId,
  cancelTestId,
  onConfirm,
  onCancel,
}: ConfirmModalProps): JSX.Element | null {
  const [value, setValue] = useState("");

  // A fresh box every time the dialog opens.
  useEffect(() => {
    if (open) setValue("");
  }, [open]);

  // Escape closes, unless a request is in flight.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !busy) onCancel();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, busy, onCancel]);

  if (!open) return null;

  const matches = typed ? value === typed.expected : true;
  const canConfirm = !busy && !disabled && matches;
  const id = `confirm-modal-${title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`;

  return (
    <div
      className="scrim"
      onMouseDown={(e) => {
        // Clicking the backdrop dismisses; clicking inside the dialog does
        // not.
        if (e.target === e.currentTarget && !busy) onCancel();
      }}
    >
      <div
        className={`modal confirm-modal ${tone}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby={`${id}-title`}
      >
        <div className="modal-head">
          <h2 id={`${id}-title`}>{title}</h2>
          <span className="spacer" />
          <button type="button" className="btn xs ghost" aria-label="Close" onClick={onCancel} disabled={busy}>
            esc ✕
          </button>
        </div>
        <div className="modal-body">
          <h3>{heading}</h3>
          {children}
          {typed ? (
            <div className="field">
              <label htmlFor={`${id}-typed`}>{typed.label}</label>
              <input
                id={`${id}-typed`}
                type="text"
                value={value}
                autoComplete="off"
                autoFocus
                placeholder={typed.placeholder ?? typed.expected}
                onChange={(e) => setValue(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && canConfirm) onConfirm(value);
                }}
              />
            </div>
          ) : null}
          {error ? (
            <div className="notice bad" role="alert">
              {error}
            </div>
          ) : null}
        </div>
        <div className="modal-foot">
          <button
            type="button"
            className={`btn ${tone}`}
            data-testid={confirmTestId}
            disabled={!canConfirm}
            onClick={() => onConfirm(value)}
            autoFocus={!typed}
          >
            {busy ? busyLabel : confirmLabel}
          </button>
          <button
            type="button"
            className="btn ghost"
            data-testid={cancelTestId}
            onClick={onCancel}
            disabled={busy}
          >
            {cancelLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
